import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useToasts } from '@air/provider-toast';
import { appendVttReloadCacheBust } from '@air/utils-video';

import { getMostRecentlyUpdatedCachedClipWithTranscriptVtt } from '~/components/AssetModalV2/Content/Panel/Transcript/utils/getMostRecentlyUpdatedCachedClipWithTranscriptVtt';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useSocketConnectionChange } from '~/providers/SocketContext/hooks/useSocketConnectionChange';
import { updateCentralizedClipAssetsAction } from '~/store/centralizedClip/actions';
import { centralizedClipIdSelector } from '~/store/centralizedClip/selectors';
import { makeTranscriptEditingTasksSelectorByWorkspaceId } from '~/store/tasks/selectors';
import { getPrivateClipTranscriptKey } from '~/swr-hooks/clips/transcript/useGetPrivateClipTranscript';
import { useUpdatePrivateClipTranscriptSegments } from '~/swr-hooks/clips/transcript/useUpdatePrivateClipTranscriptSegments';
import { isClipWithoutWorkspaceIdKey } from '~/swr-hooks/useGetClipWithNoWorkspaceId';
import { ReadyState } from '~/types/sockets';
import { useAirStore } from '~/utils/ReduxUtils';
import { usePrivateSyncTasks } from '~/utils/taskUtils/usePrivateSyncTasks';

export const usePrivateTranscriptEditingTaskEvent = () => {
  const dispatch = useDispatch();
  const store = useAirStore();
  const queryClient = useQueryClient();
  const { showToast } = useToasts();
  const { updatePrivateClipTranscriptSegments } = useUpdatePrivateClipTranscriptSegments();

  const { currentWorkspace } = useCurrentWorkspace();
  const tasksSelector = useMemo(
    () => makeTranscriptEditingTasksSelectorByWorkspaceId(currentWorkspace?.id),
    [currentWorkspace?.id],
  );

  // Tracks localTaskIds whose onComplete/onError side-effects have already been executed
  // so that concurrent invocations from syncLocalTasks polling and the
  // System.Task.completed WebSocket event don't fire duplicate toasts or refetches.
  const completedLocalTaskIds = useRef(new Set<string>());
  const failedLocalTaskIds = useRef(new Set<string>());

  const {
    loadFromStorage,
    syncLocalTasks,
    subscriptionsReady: taskSubscriptionsReady,
  } = usePrivateSyncTasks({
    workspaceId: currentWorkspace?.id,
    tasksSelector,
    localType: 'TranscriptEditing',
    remoteType: 'TranscriptEditor',
    onError: ({ localTask }) => {
      if (failedLocalTaskIds.current.has(localTask.localTaskId)) {
        return;
      }
      failedLocalTaskIds.current.add(localTask.localTaskId);
      showToast('Failed to save transcript. Please try again.', { color: 'red' });
    },
    onComplete: async ({ localTask }) => {
      if (completedLocalTaskIds.current.has(localTask.localTaskId)) {
        return;
      }
      completedLocalTaskIds.current.add(localTask.localTaskId);
      const { clipId, segments } = localTask.metadata;
      const workspaceId = currentWorkspace?.id;
      const state = store.getState();
      const isCurrentClip = centralizedClipIdSelector(state) === clipId;

      if (workspaceId) {
        await queryClient.invalidateQueries({
          queryKey: getPrivateClipTranscriptKey({ clipId, workspaceId }),
        });
      }

      if (isCurrentClip) {
        await queryClient.invalidateQueries({
          predicate: (query) => isClipWithoutWorkspaceIdKey(query.queryKey) && query.queryKey[1].clipId === clipId,
        });
      }

      // Read from react-query — Redux is still stale until PrivateAssetModal's useEffect runs.
      const refreshedTranscriptVtt = getMostRecentlyUpdatedCachedClipWithTranscriptVtt({
        queryClient,
        clipId,
      })?.assets.transcriptVtt;

      const isStillCurrentClip = centralizedClipIdSelector(state) === clipId;

      if (isStillCurrentClip && refreshedTranscriptVtt) {
        dispatch(
          updateCentralizedClipAssetsAction({
            assets: { transcriptVtt: appendVttReloadCacheBust(refreshedTranscriptVtt) },
          }),
        );
      }

      updatePrivateClipTranscriptSegments({
        clipId,
        changedSegments: segments,
      });

      showToast('Transcript saved');
    },
  });

  const syncExecutedForReadyStateRef = useRef(false);

  useEffect(() => {
    if (currentWorkspace?.id) {
      loadFromStorage();
    }
  }, [loadFromStorage, currentWorkspace?.id]);

  const syncLocalTasksOnce = useCallback(() => {
    if (!syncExecutedForReadyStateRef.current) {
      syncLocalTasks();
      syncExecutedForReadyStateRef.current = true;
    }
  }, [syncLocalTasks]);

  useEffect(() => {
    if (!taskSubscriptionsReady) {
      syncExecutedForReadyStateRef.current = false;
    }
  }, [taskSubscriptionsReady]);

  useSocketConnectionChange({
    onChange: (readyState) => {
      if (readyState === ReadyState.OPEN && taskSubscriptionsReady) {
        syncLocalTasksOnce();
      }
    },
  });

  useEffect(() => {
    if (taskSubscriptionsReady) {
      syncLocalTasksOnce();
    }
  }, [taskSubscriptionsReady, syncLocalTasksOnce]);
};
